import React from "react";
import { styled, useTheme } from "@mui/system";
import MUIRadio from "@mui/material/Radio";
import { RadioProps as RadioPropsMUI } from "@mui/material/Radio";
import SvgIcon, { SvgIconProps } from "@mui/material/SvgIcon";
export interface RadioProps extends RadioPropsMUI {}

interface SvgProps extends SvgIconProps {
  disabled?: boolean;
}

const DefaultRadionIcon = (props: SvgProps) => {
  return (
    <SvgIcon {...props}>
      <svg
        width="21"
        height="20"
        viewBox="0 0 21 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10.25 2.5C14.3921 2.5 17.75 5.85786 17.75 10C17.75 14.1421 14.3921 17.5 10.25 17.5C6.10786 17.5 2.75 14.1421 2.75 10C2.75 5.85786 6.10786 2.5 10.25 2.5Z"
          fill={props.disabled ? "#B9C8D0" : "white"}
          stroke={props.disabled ? "#B9C8D0" : "#708088"}
        />
      </svg>
    </SvgIcon>
  );
};

const SelectedRadioIcon = (props: SvgProps) => {
  return (
    <SvgIcon {...props}>
      <svg
        width="21"
        height="20"
        viewBox="0 0 21 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M10.75 2C6.33172 2 2.75 5.58172 2.75 10V10C2.75 14.4183 6.33172 18 10.75 18V18C15.1683 18 18.75 14.4183 18.75 10V10C18.75 5.58172 15.1683 2 10.75 2V2Z"
          fill={props.disabled ? "#D4EFFC" : "#0095D5"}
        />
        <path
          d="M10.75 14C12.9591 14 14.75 12.2091 14.75 10C14.75 7.79086 12.9591 6 10.75 6C8.54086 6 6.75 7.79086 6.75 10C6.75 12.2091 8.54086 14 10.75 14Z"
          fill="white"
        />
      </svg>
    </SvgIcon>
  );
};

const RadioRoot = styled(
  React.forwardRef<HTMLButtonElement, RadioProps>((props, ref) => {
    const theme = useTheme();
    const {
      icon = theme?.name === "fic" ? (
        <DefaultRadionIcon disabled={props.disabled} />
      ) : (
        props.icon
      ),
      checkedIcon = theme?.name === "fic" ? (
        <SelectedRadioIcon disabled={props.disabled} />
      ) : (
        props.checkedIcon
      ),
      ...others
    } = props;

    return (
      <MUIRadio icon={icon} checkedIcon={checkedIcon} {...others} ref={ref} />
    );
  }),
  {
    name: "Radio",
    slot: "Root",
    overridesResolver: (props, styles) => []
  }
)(({ theme }) => {
  if (theme.name === "fic") {
    return {
      "&.MuiRadio-root": {
        color: theme.palette.primary.borderDefault,
        "&:hover": {
          background: "transparent"
        },
        "&.MuiButtonBase-root": {
          padding: "0px",
          margin: "4px",
          "&.Mui-focusVisible": {
            border: "2px",
            outline: " solid " + theme.palette.primary.interactiveDefault,
            borderRadius: "50%",
            outlineOffset: "1px"
          }
        }
      },
      "&.MuiRadio-root.Mui-checked .MuiSvgIcon-root": {
        color: theme.palette.primary.interactiveDefault
      }
    };
  } else {
    return {
      "&.MuiRadio-root": {
        color: theme.palette.primary.primary,
        "&:hover": {
          background: "transparent"
        }
      },
      "&.MuiRadio-root.Mui-checked .MuiSvgIcon-root": {
        color: theme.palette.primary.blueColor
      },
      "&.MuiRadio-root.Mui-disabled .MuiSvgIcon-root": {
        color: theme.palette.primary.textDisabledColor
      },
      "&.MuiRadio-root.Mui-checked.Mui-disabled .MuiSvgIcon-root": {
        color: theme.palette.primary.textDisabledColor
      }
    };
  }
});

export const Radio = React.forwardRef<HTMLButtonElement, RadioProps>(
  function Radio(props, ref) {
    return <RadioRoot {...props} ref={ref} />;
  }
) as React.FC<RadioProps>;
